//
//  Copyright © 2023 Block, Inc. All rights reserved.
//

import PendingPromise from '../shared/pending-promise';
import posted, { Posted } from '../../../messages/envelopes/posted';
import {
  isRequestMessage,
  isErrorMessage,
  isResponseMessage,
  isAddonAction,
} from '../../../messages/envelopes/action';
import {
  RequestError,
  ErrorCode,
  RequestMessage,
  ErrorMessage,
  ResponseMessage,
  ActionName,
  ACTION_NAME_UNKNOWN,
  Message,
} from '../../../api/types/actions';
import { ReverseProxyData } from '../../../api/sdk/types';

/**
 * Message object postable to the add-on from the app.
 *
 * RequestMessage - request from the app including the
 * promiseId to be included in the ResponseMessage or ErrorMessage result posted
 * to the app from the add-on.
 *
 * ResponseMessage - result from the app resolving the request
 * from the add-on with matching promiseId.
 *
 * ErrorMessage - result from the app rejecting the request
 * from the add-on with matching promiseId.
 */
export type AddonPostableMessage =
  | RequestMessage
  | ResponseMessage
  | ErrorMessage;

/**
 * Add-on-specific delegate for resolving or rejecting a request from the app.
 *
 * @param request {RequestMessage} - Request posted to the add-on from
 * the app.
 * @param onResolve - Resolves the Promise to the app's request with a value.
 * @param onReject - Rejects the Promise to the app's request with an Error.
 */
export type OnAddonRequest = (
  request: RequestMessage,
  onResolve: (value: unknown) => void,
  onReject: (error: Error) => void
) => void | Promise<void>;

/**
 * Posts messages to the add-on from the app.
 */
export default class AddonEndpoint {
  /**
   * Promises to the add-on to be resolved with a ResponseMessage or rejected
   * with a RequestError from the app.
   */
  private pendingAppPromiseWithId: Map<string, PendingPromise>;

  private reverseProxyData?: ReverseProxyData;

  /**
   * Add-on-specific delegate for resolving or rejecting a request from the
   * app.
   */
  onAddonRequest: OnAddonRequest;

  /**
   * @param pendingAppPromiseWithId {Map<string, PendingPromise>} -
   * Promises to the add-on to be resolved with a ResponseMessage or rejected
   * with a RequestError from the app.
   * @param onAddonRequest {OnAddonRequest} - Add-on-specific delegate for
   * resolving or rejecting a request from the app.
   */
  constructor(
    pendingAppPromiseWithId: Map<string, PendingPromise>,
    onAddonRequest?: OnAddonRequest
  ) {
    this.pendingAppPromiseWithId = pendingAppPromiseWithId;
    this.onAddonRequest = onAddonRequest ?? (() => {});
  }

  private parseToMessage(stringMessage: string): AddonPostableMessage {
    const message = JSON.parse(stringMessage);
    if (message.metadata?.slotId) {
      Message.setSlotId(message.metadata.slotId);
    }

    if (isRequestMessage(message)) {
      return new RequestMessage(
        message.actionName,
        message.uuid,
        message.value
      );
    } else if (isResponseMessage(message)) {
      return new ResponseMessage(
        message.actionName,
        message.uuid,
        message.value
      );
    } else if (isErrorMessage(message)) {
      return new ErrorMessage(message.actionName, message.uuid, message.value);
    }

    throw new Error(`Unexpected message[${stringMessage}]`);
  }

  /**
   * Posts a message to the add-on from the app.
   *
   * @param stringMessage {string} - JSON string that represents either
   * RequestMessage, ResponseMessage, or ErrorMessage.
   *
   * @returns {Promise<string>} - Promise resolved with stringified object that
   * is either ResponseMessage or ErrorMessage.
   *
   */
  postMessage(stringMessage: string): Promise<string> {
    const resolvePromiseWithResultObject = async (
      onResolve: (
        value: unknown | Posted,
        actionName: ActionName,
        uuid: string
      ) => void,
      onReject: (error: Error, actionName?: ActionName, uuid?: string) => void
    ) => {
      let message: AddonPostableMessage;
      try {
        message = this.parseToMessage(stringMessage);
      } catch (error) {
        onReject(new RequestError(ErrorCode.BAD_REQUEST, error.msg));
        return;
      }

      if (isRequestMessage(message)) {
        if (!isAddonAction(message.actionName)) {
          onReject(
            new RequestError(
              ErrorCode.BAD_REQUEST,
              `Invalid action name: ${message.actionName}`
            ),
            message.actionName,
            message.uuid
          );
          return;
        }

        await this.onAddonRequest(
          message,
          (value: unknown) => {
            onResolve(value, message.actionName, message.uuid);
          },
          (error: Error) => {
            onReject(error, message.actionName, message.uuid);
          }
        );

        onReject(
          new RequestError(
            ErrorCode.NOT_FOUND,
            `Action '[${message.actionName}]' unhandled by the client.`
          ),
          message.actionName,
          message.uuid
        );
      }

      if (isResponseMessage(message) || isErrorMessage(message)) {
        const pendingAppPromiseId = message.uuid;

        // There should be a pendingAppPromiseId or there's a problem.
        if (pendingAppPromiseId === undefined) {
          onReject(
            new RequestError(
              ErrorCode.BAD_REQUEST,
              'Unexpected undefined pendingAppPromiseId'
            ),
            message.actionName,
            message.uuid
          );
          return;
        }

        const pendingAppPromise =
          this.pendingAppPromiseWithId.get(pendingAppPromiseId);

        if (pendingAppPromise === undefined) {
          onReject(
            new RequestError(
              ErrorCode.BAD_REQUEST,
              `Unexpected undefined promise[${pendingAppPromiseId}]`
            ),
            message.actionName,
            message.uuid
          );
          return;
        }

        // Clean up the pendingAppPromiseId.
        this.pendingAppPromiseWithId.delete(pendingAppPromiseId);

        // Resolve the pendingAppPromiseId.
        if (isResponseMessage(message)) {
          pendingAppPromise.onResolve(message);
        }

        // Reject the pendingAppPromiseId.
        if (isErrorMessage(message)) {
          pendingAppPromise.onReject(
            new RequestError(
              message.value.errorCode as ErrorCode,
              message.value.errorMessage
            )
          );
        }

        // Resolve the post of Success or Failure with a Posted.
        onResolve(posted, message.actionName, message.uuid);
      }
    };

    return new Promise((onResolve, onReject) => {
      resolvePromiseWithResultObject(
        (value: unknown, actionName: ActionName, uuid: string) => {
          onResolve(
            JSON.stringify(
              new ResponseMessage(
                actionName,
                uuid,
                typeof value === 'object' ? value : { value }
              )
            )
          );
        },
        (error: Error, actionName?: ActionName, uuid?: string) => {
          onReject(
            JSON.stringify(
              new ErrorMessage(actionName ?? ACTION_NAME_UNKNOWN, uuid, {
                errorCode:
                  error instanceof RequestError
                    ? error.errorCode
                    : ErrorCode.UNKNOWN,
                errorMessage: error.message,
              })
            )
          );
        }
      );
    });
  }

  /**
   * Returns validated reverse proxy data. This method does not guarantee that
   * the token has not expired. If no reverse proxy data is set, an Error is thrown.
   *
   * @returns The reverse proxy data object, which includes the token and
   *          the url that can be used to construct a Square reverse proxy URL.
   * @throws  {RequestError} when the reverse proxy data is not set.
   */
  getReverseProxyData(): ReverseProxyData {
    if (!this.reverseProxyData) {
      throw new RequestError(
        ErrorCode.UNAUTHORIZED,
        'No reverse proxy data found!'
      );
    }

    return this.reverseProxyData;
  }

  /**
   * Setter for the reverse proxy data. The reverse proxy data must include the
   * `token` and `url` fields. If these fields are not provided, an Error is thrown.
   *
   * @param reverseProxyData  The reverse proxy data provided by the point of sale at runtime.
   * @throws {RequestError} when the reverse proxy data is malformed.
   */
  setReverseProxyData(reverseProxyData: ReverseProxyData): void {
    if (!reverseProxyData.token || !reverseProxyData.url) {
      throw new RequestError(
        ErrorCode.UNAUTHORIZED,
        `Cannot construct the reverse proxy URL. Reverse proxy data was: ${JSON.stringify(
          reverseProxyData
        )}`
      );
    }

    this.reverseProxyData = reverseProxyData;
  }
}
